import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import type {
    ProjectGasResults,
    ProjectTotalResults,
    ProjectPayload,
    FastProjectsResponse,
    SOCRefProps,
    SOCRefResponse,
    UpdateProjectPayload,
    ProjectUser,
    GetProjectsPayload,
    ProjectAttachment,
    UploadAttachmentPayload,
    GetProjectResultsPayload,
    DownloadProjectReportPayload,
    ReportDownload,
    ReportTemplate,
    ProjectLockInfo,
    GetProjectsInfinitePayload,
    ImportProjectPayload,
    ImportProjectResponse,
} from "./projectTypes";
import type { CopyAsyncResponse, ReportAsyncPayload, ReportAsyncResponse } from "../asyncJobs/asyncJobsTypes";
import type { Option } from "@typedefs/modulesInterfaces";
import { fileNameFromContentDisposition } from "@utils/helperFunctions";
import type { TagTypes } from "../../apiSlice";
import type { ApiSliceType, PublicApiSliceType, Project, ProjectTotals } from "@typedefs/interfaces";

// Define separate tag types for fast and detailed views
const projectTags = {
    FAST_LIST: 'FastProjects' as TagTypes,
    FAST_DETAIL: (id: number) => ({ type: 'FastProjects' as TagTypes, id }),
    DETAIL_LIST: 'DetailedProjects' as TagTypes,
    DETAIL: (id: number) => ({ type: 'DetailedProjects' as TagTypes, id })
};

interface MoveToTrashbinPayload {
    id: number;
    restore?: boolean;
}

const INCLUDED_NARRATIVE_LANGUAGES = ['en', 'es', 'fr'];

export function createProjectApi(apiSlice: ApiSliceType | PublicApiSliceType) {
    const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
        getProjects: builder.query<FastProjectsResponse, GetProjectsPayload>({
            query: ({ url, showFinalized, tags, showTrashbin }: GetProjectsPayload) => {
                const params = new URLSearchParams();
                if (tags) params.append("tags", tags.join(","));
                params.append("is_archived", showTrashbin ? "true" : "false");
                params.append("is_finalized", showFinalized ? "true" : "false");

                return `${url ? `${url}&` : "projects/?"}${params.toString()}`
            },
            providesTags: (results: FastProjectsResponse | undefined, _error, _arg) => [
                projectTags.FAST_LIST,
                ...(results?.results.map(project => projectTags.FAST_DETAIL(project.id)) ?? []).map(tag => tag)
            ],
        }),

        getProjectsInfinite: builder.infiniteQuery<
            FastProjectsResponse,
            GetProjectsInfinitePayload,
            number
        >({
            query: ({ queryArg, pageParam }) => {
                const { showFinalized, tags, showTrashbin, page_size } = queryArg;
                const params = new URLSearchParams();

                // Add pagination params
                params.append("page", pageParam.toString());
                params.append("page_size", page_size?.toString() ?? "15");

                // Add filtering params
                if (tags) params.append("tags", tags.join(","));
                params.append("is_archived", showTrashbin ? "true" : "false");
                params.append("is_finalized", showFinalized ? "true" : "false");

                return `projects/?${params.toString()}`;
            },
            infiniteQueryOptions: {
                initialPageParam: 1,
                getNextPageParam: (lastPage, allPages, lastPageParam) => {
                    // If there's no next URL, we've reached the end
                    if (!lastPage.next) {
                        return undefined;
                    }

                    // Extract page number from next URL or calculate next page
                    try {
                        const nextUrl = new URL(lastPage.next);
                        const nextPageParam = nextUrl.searchParams.get('page');
                        return nextPageParam ? parseInt(nextPageParam, 10) : lastPageParam + 1;
                    } catch {
                        // Fallback: calculate next page
                        return lastPageParam + 1;
                    }
                },
                getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
                    // If there's no previous URL or we're on page 1, no previous page
                    if (!firstPage.previous || firstPageParam <= 1) {
                        return undefined;
                    }

                    // Extract page number from previous URL or calculate previous page
                    try {
                        const prevUrl = new URL(firstPage.previous);
                        const prevPageParam = prevUrl.searchParams.get('page');
                        return prevPageParam ? parseInt(prevPageParam, 10) : firstPageParam - 1;
                    } catch {
                        // Fallback: calculate previous page
                        return firstPageParam - 1;
                    }
                },
                // Optional: limit the number of pages kept in cache to manage memory
                maxPages: 10,
            },
            providesTags: (results, _error, _arg) => [
                projectTags.FAST_LIST,
                ...(results?.pages?.flatMap(page => page?.results?.map(project => projectTags.FAST_DETAIL(project.id)) ?? []) ?? []).map(tag => tag)
            ],
        }),

        // Detailed project query
        getProjectById: builder.query<Project, string>({
            query: (id: string) => `projects/${id}/`,
            providesTags: (result: Project | undefined) => result
                ? [
                    projectTags.DETAIL(result.id),
                    projectTags.FAST_DETAIL(result.id)
                ]
                : [],
        }),

        getProjectResults: builder.query<ProjectTotalResults, GetProjectResultsPayload>({
            query: ({ id, activities }: GetProjectResultsPayload) => {
                if (!id || id === 0) {
                    throw new Error('Invalid project ID');
                }
                return activities
                    ? `projects/${id}/results/?activities=${activities?.join(",")}`
                    : `projects/${id}/results/`;
            },
            providesTags: (_result, _error, { id }: GetProjectResultsPayload) => [
                { type: "ProjectResults" as TagTypes, id },
            ],
        }),

        getProjectResultsByGas: builder.query<ProjectGasResults, GetProjectResultsPayload>({
            query: ({ id, activities }: GetProjectResultsPayload) => {
                if (!id || id === 0) {
                    throw new Error('Invalid project ID');
                }
                return activities
                    ? `projects/${id}/results/?activities=${activities?.join(",")}&aggregate=gas`
                    : `projects/${id}/results/?aggregate=gas`;
            },
            providesTags: (_result, _error, { id }: GetProjectResultsPayload) => [
                { type: "ProjectResults" as TagTypes, id },
            ],
        }),

        getSOCref: builder.query<SOCRefResponse | null, void | SOCRefProps>({
            query: (props?: SOCRefProps) => {
                if (!props) return "ipcc/soil-organic-carbons/";
                const { climate, moisture, soil_type } = props;
                const climateStr = climate ? `climate=${climate}&` : "";
                const moistureStr = moisture ? `moisture=${moisture}&` : "";
                const soil_typeStr = soil_type ? `soil_type=${soil_type}` : "";
                return `ipcc/soil-organic-carbons/?${climateStr}${moistureStr}${soil_typeStr}`;
            },
            transformResponse: (response: SOCRefResponse[]) =>
                response?.length ? response[0] : null,
        }),

        getProjectMemberships: builder.query<ProjectUser[], number>({
            query: (id: number) => `projects/${id}/memberships/`,
            providesTags: ['ProjectUsers' as TagTypes],
        }),

        getMyMemberships: builder.query<ProjectUser[], { projectId: number, userId: number }>({
            query: ({ projectId, userId }: { projectId: number, userId: number }) => `projects/${projectId}/memberships/?user=${userId}`,
            transformResponse: (response: ProjectUser[], _meta: any, { userId }: { projectId: number, userId: number }) => {
                return response.filter((member) => member.user === userId)
            },
            providesTags: (result, _error, { projectId, userId }: { projectId: number, userId: number }) => result ? [{ type: 'ProjectUsers' as TagTypes, id: `${projectId}-${userId}` }] : [],
        }),

        getProjectAttachments: builder.query<ProjectAttachment[], number>({
            query: (projectId: number) => `project-attachments/?project_id=${projectId}`,
            providesTags: (result) =>
                result
                    ? [...result.map(({ id }) => ({ type: 'ProjectAttachments' as TagTypes, id })), { type: 'ProjectAttachments' as TagTypes, id: 'LIST' }]
                    : [{ type: 'ProjectAttachments' as TagTypes, id: 'LIST' }],
        }),

        downloadProjectAttachment: builder.query<Blob, number>({
            query: (id: number) => ({
                url: `project-attachments/${id}/download/`,
                method: 'GET',
                responseHandler: (response: Response) => response.blob(),
            }),
        }),

        downloadProjectReport: builder.query<ReportDownload, DownloadProjectReportPayload>({
            query: ({ id, activities, template, content, currentLanguage }: DownloadProjectReportPayload) => {
                const params = new URLSearchParams();

                if (activities) params.append('activities', activities.join(","));
                if (template) params.append('template', template);

                const currentLangIsIncluded = INCLUDED_NARRATIVE_LANGUAGES.includes(currentLanguage);
                const acceptLanguage = currentLangIsIncluded ? currentLanguage : 'en';

                return {
                    url: `projects/${id}/report/?${params.toString()}`,
                    method: 'POST',
                    ...(content && { body: { content } }),
                    responseHandler: (response: Response) => response.blob(),
                    headers: acceptLanguage ? {
                        'Accept-Language': acceptLanguage,
                    } : {}
                }
            },
            transformResponse: (blob: Blob, meta) => ({
                blob,
                fileName: fileNameFromContentDisposition(meta?.response?.headers.get('Content-Disposition')),
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error: any) {
                    if (error?.data instanceof Blob) {
                        const text = await error.data.text();
                        try {
                            const json = JSON.parse(text);
                            error.data = json;
                        } catch {
                            error.data = { message: 'Error downloading report' };
                        }
                    }
                }
            }
        }),

        getAllTags: builder.query<Option[], void>({
            query: () => "projects/tags/",
            providesTags: ['Tags' as TagTypes],
        }),

        getTagsByProject: builder.query<Option[], number>({
            query: (projectId: number) => `projects/${projectId}/tags/`,
            providesTags: (result, _error, projectId: number) =>
                result
                    ? [
                        ...result.map(tag => ({ type: 'ProjectTags' as TagTypes, id: tag.id })),
                        { type: 'ProjectTags' as TagTypes, id: projectId }
                    ]
                    : [{ type: 'ProjectTags' as TagTypes, id: projectId }]
        }),

        getProjectLockInfo: builder.query<ProjectLockInfo, string>({
            query: (projectId: string) => `projects/${projectId}/lock/`,
        }),

        getProjectTotals: builder.query<ProjectTotals, string>({
            async queryFn(projectId: string, _queryApi, _extraOptions, baseQuery) {
                const totalHectares = baseQuery(`projects/${projectId}/total-hectares/`);
                const totalCatch = baseQuery(`projects/${projectId}/total-catch/`);
                const totalLivestock = baseQuery(`projects/${projectId}/total-livestock/`);

                const [totalHectaresResult, totalCatchResult, totalLivestockResult] = await Promise.all([
                    totalHectares,
                    totalCatch,
                    totalLivestock
                ]);

                if (totalHectaresResult.error) return { error: totalHectaresResult.error };
                if (totalCatchResult.error) return { error: totalCatchResult.error };
                // if (totalLivestockResult.error) return { error: totalLivestockResult.error };

                const data = {
                    total_hectares: totalHectaresResult.data.total_hectares || null,
                    total_catch: totalCatchResult.data || null,
                    total_livestock: totalLivestockResult.data || null
                };

                return { data };
            },
        }),

        //MUTATIONS!
        createProject: builder.mutation<Project, ProjectPayload>({
            query: (data: ProjectPayload) => ({
                url: "projects/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: [
                projectTags.FAST_LIST,
                projectTags.DETAIL_LIST
            ],
        }),

        updateProject: builder.mutation<Project, UpdateProjectPayload>({
            query: ({ id, body }: UpdateProjectPayload) => ({
                url: `projects/${id}/`,
                method: "PATCH",
                body: { ...body, id },
            }),
            invalidatesTags: (_results, _error, { id }: UpdateProjectPayload) => [
                projectTags.DETAIL(id),
                projectTags.FAST_DETAIL(id),
                // Only invalidate lists if the update affects list view (e.g., name change)
                projectTags.FAST_LIST,
                projectTags.DETAIL_LIST,
                // Project results (getProjectResults, getProjectResultsByGas) - depend on project settings
                { type: "ProjectResults" as TagTypes, id },
                // Module results - tagged by path+uniqueId; we can't know which modules belong to this project,
                // so invalidate all to ensure consistency when project settings (e.g. climate, soil) change
                { type: "ModuleResults" as TagTypes },
            ],
        }),

        // Map data specific mutation
        uploadMapData: builder.mutation<void, { projectId: number, mapData: string | null }>({
            query: ({ projectId, mapData }: { projectId: number, mapData: string | null }) => ({
                url: `projects/${projectId}/`,
                method: "PATCH",
                body: { map_data: mapData }
            }),
            invalidatesTags: (_result, _error, { projectId }: { projectId: number, mapData: string | null }) => [
                projectTags.DETAIL(projectId)
            ]
        }),

        deleteProject: builder.mutation<Project, number>({
            query: (id: number) => ({
                url: `projects/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, id: number) => [
                projectTags.DETAIL(id),
                projectTags.FAST_DETAIL(id),
                projectTags.FAST_LIST,
                projectTags.DETAIL_LIST
            ],
        }),

        finalizeProject: builder.mutation<Project, number>({
            query: (id: number) => ({
                url: `projects/${id}/`,
                method: "PATCH",
                body: {
                    is_finalized: true
                }
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),

        // Upload a new attachment
        uploadProjectAttachment: builder.mutation<ProjectAttachment, UploadAttachmentPayload>({
            query: ({ project, file }: UploadAttachmentPayload) => {
                const formData = new FormData();
                formData.append('project', project.toString());
                formData.append('file', file);

                return {
                    url: 'project-attachments/',
                    method: 'POST',
                    body: formData,
                    formData: true,
                };
            },
            invalidatesTags: [{ type: 'ProjectAttachments' as TagTypes, id: 'LIST' }],
        }),

        // Delete an attachment
        deleteProjectAttachment: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `project-attachments/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id: number) => [
                { type: 'ProjectAttachments' as TagTypes, id },
                { type: 'ProjectAttachments' as TagTypes, id: 'LIST' },
            ],
        }),

        createProjectCopy: builder.mutation<Project, number>({
            query: (id: number) => ({
                url: `projects/${id}/copy/`,
                method: "POST",
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),

        deleteMembership: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `project-memberships/${id}/`,
                method: "DELETE"
            }),
            invalidatesTags: ['ProjectUsers' as TagTypes],
        }),

        addTagToProject: builder.mutation<void, { projectId: number, name: string }>({
            query: ({ projectId, name }: { projectId: number, name: string }) => ({
                url: `projects/${projectId}/tags/`,
                method: "POST",
                body: { name }
            }),
            invalidatesTags: (_result, _error, { projectId }: { projectId: number, name: string }) => [
                { type: 'ProjectTags' as TagTypes, id: projectId }, 'Tags'
            ],
        }),

        deleteTagFromProject: builder.mutation<void, { projectId: number, tagId: number }>({
            query: ({ projectId, tagId }: { projectId: number, tagId: number }) => ({
                url: `projects/${projectId}/tags/${tagId}/`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, { projectId }: { projectId: number, tagId: number }) => [
                { type: 'ProjectTags' as TagTypes, id: projectId },
                'Tags' as TagTypes
            ]
        }),

        moveToTrashbin: builder.mutation<Project, MoveToTrashbinPayload>({
            query: ({ id, restore }: MoveToTrashbinPayload) => ({
                url: `projects/${id}/`,
                method: "PATCH",
                body: {
                    is_archived: restore ? false : true
                }
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),

        togglePublicProject: builder.mutation<Project, { projectId: number, isPublic: boolean }>({
            query: ({ projectId, isPublic }: { projectId: number, isPublic: boolean }) => ({
                url: `projects/${projectId}/`,
                method: "PATCH",
                body: { is_public: isPublic }
            }),
            invalidatesTags: (_result, _error, { projectId }) => [projectTags.DETAIL(projectId)],
        }),

        toggleProjectLock: builder.mutation<ProjectLockInfo, { projectId: number, isLocked: boolean }>({
            query: ({ projectId, isLocked }: { projectId: number, isLocked: boolean }) => ({
                url: `projects/${projectId}/${isLocked ? "lock" : "unlock"}/`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, { projectId }) => [projectTags.DETAIL(projectId)],
        }),

        copyProjectAsync: builder.mutation<CopyAsyncResponse, number>({
            query: (projectId) => ({
                url: `projects/${projectId}/copy/async/`,
                method: 'POST',
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),

        triggerAsyncReport: builder.mutation<ReportAsyncResponse, ReportAsyncPayload>({
            query: ({ id, activities, template, content, currentLanguage }) => {
                const params = new URLSearchParams();
                if (activities) params.append('activities', activities.join(','));
                if (template) params.append('template', template);

                return {
                    url: `projects/${id}/report/async/?${params.toString()}`,
                    method: 'POST',
                    ...(content && { body: { content } }),
                    headers: currentLanguage ? { 'Accept-Language': currentLanguage } : {},
                };
            },
        }),

        getReportTemplates: builder.query<ReportTemplate[], void>({
            query: () => 'report-templates/',
        }),

        exportProject: builder.query<Blob, { id: number; projectName: string }>({
            query: ({ id }) => ({
                url: `projects/${id}/export/`,
                responseHandler: (response: Response) => response.blob(),
            }),
            async onQueryStarted({ projectName }, { queryFulfilled }) {
                try {
                    const { data: blob } = await queryFulfilled;
                    // Trigger download using browser-native approach
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `${projectName}.exactp`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } catch (error) {
                    console.error("Export failed:", error);
                }
            },
        }),

        importProject: builder.mutation<ImportProjectResponse, ImportProjectPayload>({
            query: ({ data, forceCopy }: ImportProjectPayload) => ({
                url: `projects/import_project/${forceCopy ? '?forceCopy=true' : ''}`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),
    });

    return apiSlice.injectEndpoints({ endpoints });
} 